import { Card } from "@/components/ui/card";
import { DollarSign, TrendingUp } from "lucide-react";
import { formatCurrency } from "@/lib/utils";

// Tier display names
const TIER_NAMES: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  expert: 'Expert',
} as const;

interface SupplementPricingProps {
  /** Number of unmatched line items from the selected comparison */
  unmatchedLineItems: number;
  /** Total cost in cents (from API estimate) */
  costCents: number;
  /** Current subscription tier code */
  tierCode: string;
  /** Optional className for styling */
  className?: string;
}

/**
 * Displays pricing breakdown for supplement generation
 * Shows: line item count, credits used, cost per credit, and total estimated cost
 */
export function SupplementPricing({ 
  unmatchedLineItems,
  costCents,
  tierCode,
  className 
}: SupplementPricingProps) {
  const totalCost = costCents / 100; // Convert cents to dollars
  const costPerItem = unmatchedLineItems > 0 ? totalCost / unmatchedLineItems : 0;
  
  // Get tier display name - tierCode comes from API so it's always valid
  const tierName = TIER_NAMES[tierCode.toLowerCase()];

  return (
    <Card className={className}>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 pb-2 border-b">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Estimated Cost</h3>
        </div>

        {/* Pricing Breakdown */}
        <div className="space-y-3">
          {/* Line Items */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Unmatched Line Items:</span>
            <span className="font-medium">{unmatchedLineItems.toLocaleString()}</span>
          </div>

          {/* Cost Per Line Item */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Cost per Line Item:</span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                {tierName}
              </span>
            </div>
            <span className="font-medium">{formatCurrency(costPerItem)}</span>
          </div>

          {/* Divider */}
          <div className="border-t pt-3">
            {/* Total Cost */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">Total Estimated Cost:</span>
              </div>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(totalCost)}
              </span>
            </div>
          </div>
        </div>

        {/* Info Text */}
        <p className="text-xs text-muted-foreground pt-2 border-t">
          This cost will be charged only when the supplement analysis completes successfully. No charge if it fails.
        </p>
      </div>
    </Card>
  );
}
